import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fullName } from "@torre/team";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import AssignSquadModal from "../components/AssignSquadModal";
import {
  fetchSquads,
  createSquad,
  updateSquad,
  deleteSquad,
  fetchTeamMembers,
} from "../services/teamService";
import type { Squad, SquadMember, TeamMember } from "../types";
import { useStaffAccess } from "../../../hooks/useIsStaffAdmin";

// Gestión de squads (AgroCloud#12): alta de squads con su jefe y miembros, y
// asignación del squad completo a una tarea/tarea o release.
//
// Supersede el PR AgroCloud#98 (rama ai/issue-12-gestion-de-squads): misma historia,
// pero respaldada por el backend (`squads` + `POST /squadAssign`) en
// lugar de un store con datos mock, e integrada en el módulo completo.
export default function SquadsSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  const { isStaffAdmin, ownerPolicy } = useStaffAccess();
  const { t } = useTranslation("common");
  const [rows, setRows] = useState<Row[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [assigning, setAssigning] = useState<Squad | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [squads, membersPage] = await Promise.all([
        fetchSquads(),
        fetchTeamMembers(),
      ]);
      setTeamMembers(membersPage.items);
      setRows(squads.items as unknown as Row[]);
    } catch {
      setError(t("team.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Cualquier colaborador activo puede liderar o integrar un squad: antes solo
  // salían los clasificados como tech lead y, sin ninguno, la lista quedaba vacía.
  const memberOptions = useMemo(
    () =>
      teamMembers
        .filter((w) => w.status !== "offboarded" && w.status !== "inactive")
        .map((w) => ({ value: w.id, label: fullName(w) || w.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teamMembers],
  );
  const membersById = useMemo(
    () => new Map(teamMembers.map((w) => [w.id, w])),
    [teamMembers],
  );

  const columns: Column[] = [
    { key: "name", label: t("team.fields.squadName") },
    {
      key: "leadName",
      label: t("team.fields.lead"),
      render: (r) => String(r.leadName ?? "—"),
    },
    {
      key: "members",
      label: t("team.fields.members"),
      align: "center",
      render: (r) => String((r.members as SquadMember[] | undefined)?.length ?? 0),
    },
    {
      key: "active",
      label: t("team.fields.active"),
      align: "center",
      render: (r) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] ${
            r.active === false
              ? "bg-gray-400/15 text-gray-500"
              : "bg-emerald-500/15 text-emerald-500"
          }`}
        >
          {r.active === false ? t("team.no") : t("team.si")}
        </span>
      ),
    },
  ];

  const fields: FormField[] = [
    { key: "name", label: t("team.fields.squadName"), required: true },
    {
      key: "leadId",
      label: t("team.fields.lead"),
      type: "select",
      options: memberOptions,
      hint: t("team.hints.lead"),
    },
    { key: "projectId", label: t("team.fields.project") },
    {
      key: "members",
      label: t("team.fields.members"),
      type: "repeat",
      addRowLabel: t("team.addMember"),
      // Se elige de la lista de colaboradores; el nombre y el rol se llenan solos.
      repeatColumns: [
        {
          key: "teamMemberId",
          label: t("team.fields.squadMember"),
          type: "select",
          options: memberOptions,
          unique: true,
          placeholder: t("team.pickTeamMember"),
        },
      ],
    },
    { key: "notes", label: t("team.fields.notes"), type: "textarea" },
  ];

  const toPayload = (values: Record<string, string>): Partial<Squad> => {
    let members: SquadMember[] = [];
    try {
      const parsed = JSON.parse(values.members || "[]");
      if (Array.isArray(parsed)) members = parsed as SquadMember[];
    } catch {
      members = [];
    }
    // Filas sin elegir se descartan; nombre y rol salen del padrón (si el
    // colaborador ya no está en la lista, se conserva lo que traía la fila).
    const seen = new Set<string>();
    members = members
      .filter((m) => m && m.teamMemberId && !seen.has(m.teamMemberId) && seen.add(m.teamMemberId))
      .map((m) => {
        const w = membersById.get(m.teamMemberId);
        return {
          teamMemberId: m.teamMemberId,
          name: w ? fullName(w) : (m.name ?? null),
          role: w ? (w.role ?? null) : (m.role ?? null),
        };
      });
    return {
      name: values.name,
      leadId: values.leadId || null,
      projectId: values.projectId || null,
      members,
      notes: values.notes || null,
    };
  };

  return (
    <>
      <ApiCrudSection
        readOnly={!isStaffAdmin && ownerPolicy}
        title={t("team.menu.squads")}
        subtitle={t("team.squadsSubtitle")}
        icon="ri-group-2-line"
        columns={columns}
        fields={fields}
        rows={rows}
        loading={loading}
        error={error}
        searchKey="name"
        addLabel={t("team.newSquad")}
        // Acción propia de la historia: asignar el squad a una tarea/release.
        extraActions={isStaffAdmin ? (row) => (
          <button
            title={t("team.assign")}
            aria-label={`${t("team.assign")} ${String(row.name ?? "")}`}
            onClick={() => setAssigning(row as unknown as Squad)}
            className="rounded-md p-1.5 text-emerald-500 hover:bg-emerald-500/10"
          >
            <i className="ri-user-shared-line" />
          </button>
        ) : undefined}
        onCreate={async (values) => {
          await createSquad(toPayload(values));
          await load();
        }}
        onUpdate={async (id, values) => {
          await updateSquad(id, toPayload(values));
          await load();
        }}
        onDelete={async (id) => {
          await deleteSquad(id);
          await load();
        }}
      />

      {assigning && (
        <AssignSquadModal
          squad={assigning}
          onClose={() => setAssigning(null)}
          onAssigned={() => void load()}
        />
      )}
    </>
  );
}
